import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import LandingHeader from '../components/LandingHeader';
import Footer from '../components/Footer';
import { BACKEND_URL } from '../config';

/**
 * /install/:slug — Share install route.
 * If logged out → redirect to register with redirect back.
 * If logged in → redirect to internal marketplace install flow.
 */
export default function InstallRedirect() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { user, authStatus } = useAuth();
  const isAuthenticated = authStatus === 'authenticated' && user;
  const [checking, setChecking] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    handleInstall();
  }, [authStatus]);

  const handleInstall = async () => {
    if (authStatus === 'loading') return;

    if (!isAuthenticated) {
      // Store install intent for seamless post-login install
      localStorage.setItem('install_intent', JSON.stringify({ slug, timestamp: Date.now() }));
      localStorage.setItem('post_auth_redirect', `/marketplace/${slug}?auto_install=true`);
      navigate(`/register?redirect=${encodeURIComponent(`/marketplace/${slug}?auto_install=true`)}`, { replace: true });
      return;
    }

    // User is logged in — look up the template ID and redirect to marketplace install
    try {
      const res = await fetch(`${BACKEND_URL}/api/public/automations/${slug}`);
      if (!res.ok) {
        setError('Automation not found');
        setChecking(false);
        return;
      }
      const data = await res.json();
      const templateId = data.template?.id;

      if (templateId) {
        navigate(`/partner-pro/ai-workforce/marketplace-v2?install=${templateId}`, { replace: true });
      } else {
        // Fallback: go to the detail page
        navigate(`/automations/${slug}`, { replace: true });
      }
    } catch (e) {
      console.error('Install redirect error:', e);
      setError('Something went wrong');
      setChecking(false);
    }
  };

  if (error) {
    return (
      <div className="min-h-screen bg-[#161B26]">
        <LandingHeader />
        <div className="flex flex-col items-center justify-center pt-40 px-6">
          <h1 className="text-2xl font-bold text-white mb-3">Automation not found</h1>
          <p className="text-[#8b8fa3] text-sm mb-6">This install link may be invalid or the automation was removed.</p>
          <button
            onClick={() => navigate('/automations')}
            className="text-[#EC4899] text-sm hover:underline"
          >
            Browse all automations
          </button>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#161B26] flex items-center justify-center">
      <div className="text-center">
        <div className="w-8 h-8 border-2 border-[#EC4899] border-t-transparent rounded-full animate-spin mx-auto mb-4" />
        <p className="text-[#8b8fa3] text-sm">Preparing installation...</p>
      </div>
    </div>
  );
}
